import { omit } from "lodash";
import { yearToDateString } from "utils/date";

import { isMixedBuilding } from "../model/mix";
import {
  type APISubmitPayload,
  type APIUpdatePayload,
  type EnergyAnalysisFileSection,
  isFileFromLocal,
  type MixUseKey,
  type ModernizationElement,
  type SingleBuildingFormState,
} from "../model/types";

const modernizationElementToPayload = (modernizationElement: ModernizationElement | undefined): string => {
  const dateString = yearToDateString(modernizationElement?.modernization_date);
  if (!modernizationElement?.modernized) return "";
  return dateString ?? "";
};

export const convertFormDataToPayload = (
  formData: SingleBuildingFormState,
  dtCreation: boolean
): APISubmitPayload | undefined => {
  const bulidingType = formData.details?.building_type;
  const isMixed = isMixedBuilding(bulidingType);

  const buildingTypeMix = !bulidingType
    ? []
    : !isMixed
    ? [{ percentage: 100, type: bulidingType }]
    : Object.entries(formData.details?.mixConfiguration ?? {}).map(([type, percentage]) => ({
        percentage: percentage ?? 0,
        type: type as MixUseKey,
      }));

  const payload: APISubmitPayload = {
    buildingGeoLocation: {
      polygon: formData.outline ?? [],
    },
    buildingType: buildingTypeMix,
    city: formData.addressInfo?.city ?? "",
    constructionYear: formData.details?.construction_year,
    coolingAvailable: formData.extraDetails?.has_cooling,
    country: formData.addressInfo?.country ?? "",
    dtCreation,
    EPCFields: formData.epcRawExtractionData,
    fileInfo: Object.entries(formData.files ?? {}).map(([section, files]) => ({
      [section]: files.map((f) => f.cloudPath ?? ""),
    })) as Record<EnergyAnalysisFileSection, string[]>[],
    floorAmount: formData.details?.no_of_floors,
    hasModernizations: formData.extraDetails?.modernized,
    heatingSystem: formData.extraDetails?.heating_system,
    heatingSystemInstalledAt: yearToDateString(formData.extraDetails?.installation_date),
    imageList: formData.images.filter(isFileFromLocal).map((image) => image.cloudPath ?? ""),
    latitude: formData.buildingCenter.lat,
    layerInfo: formData.layers?.map((layer) => ({
      layerName: layer.name || layer.preset,
      sublayers: layer.subLayers.map((subLayer) => ({
        name: subLayer.name,
        objects: subLayer.objects.map((object) => ({
          name: object.name,
          polygon: object.points,
        })),
      })),
    })),
    longitude: formData.buildingCenter.lng,
    mechanicalVentilation: formData.extraDetails?.mechanical_ventilation,
    modernizationFacadeAt: modernizationElementToPayload(formData.extraDetails?.modernizationElements?.facade),
    modernizationRooftopAt: modernizationElementToPayload(formData.extraDetails?.modernizationElements?.rooftop),
    modernizationWindowsAt: modernizationElementToPayload(formData.extraDetails?.modernizationElements?.windows),
    name: formData.addressInfo?.name ?? "",
    postCode: formData.addressInfo?.postal_code ?? "",
    pvSystemAvailable: formData.extraDetails?.has_pv,
    rentalUnits: formData.details?.rental_units,
    street: formData.addressInfo?.address ?? "",
  };
  return payload;
};

export const convertFormDataToUpdatePayload = (
  formData: SingleBuildingFormState,
  projectId: string,
  dtCreation: boolean,
  isEpcExtractionAvailable: boolean
): APIUpdatePayload | undefined => {
  const submitPayload = convertFormDataToPayload(formData, dtCreation);
  if (!submitPayload) return;
  const updatePayload = {
    ...omit({ ...submitPayload }, isEpcExtractionAvailable ? [] : "EPCFields"),
    projectId,
  };
  return updatePayload;
};
